import { formatDate } from '@angular/common';
import { computed, inject, LOCALE_ID, Signal, signal } from '@angular/core';
import { form, validate } from '@angular/forms/signals';
import { TimeReportSeriesFeatureService } from '@app/shared/services/time-report-series-feature.service';
import { SeriesWeekday, TimeReportSeries } from '@app/shared/types/models/reports/time-report-series';
import { TimePickerValue } from '@app/shared/types/models/shared/time-picker-value';
import { ProffeoUiDateRange } from '@app/shared/ui/types/date-picker.types';
import {
  dailyRangeMinutes,
  DEFAULT_SERIES_BREAK_MINUTES,
  DEFAULT_SERIES_WEEKDAYS,
  includedWeekdayMask,
  MAX_SERIES_BREAK_MINUTES,
  minutesToIsoDuration,
  SeriesErrorKind,
  seriesOccurrenceDates,
  suggestedBreakStart,
  toClockValue,
  toLocalIsoDate,
  validateSeriesInput
} from '@app/shared/utils/time-report-series.utils';

export interface ReportSeriesModel {
  enabled: boolean;
  dateRange: ProffeoUiDateRange;
  weekdays: SeriesWeekday[];
  breakEnabled: boolean;
  breakMinutes: number;
}

export interface ReportSeriesDailyRange {
  from: TimePickerValue;
  to: TimePickerValue;
}

/** What the hosting dialog tells the shared series form. */
export interface ReportSeriesContext {
  /** Whether this dialog instance may offer a series at all (create only, never Timer-only). */
  canUse: () => boolean;
  /** Daily from/to while the host is in Range mode; `undefined` in Manual mode. */
  dailyRange: () => ReportSeriesDailyRange | undefined;
  /** Date range the series starts from when the user switches it on. */
  initialRange: () => ProffeoUiDateRange;
}

const SERIES_ERROR_KEYS: Record<SeriesErrorKind, string> = {
  seriesRangeRequired: 'ui-forms-this-field-is-required',
  seriesInvalidRange: 'time-reports.series.invalid-date-range',
  seriesTooLong: 'time-reports.series.maximum-days',
  seriesWeekdaysRequired: 'time-reports.series.weekdays-required',
  seriesNoDays: 'time-reports.series.no-days',
  seriesInvalidDailyRange: 'time-reports-form-modal-the-time-range-is-not-valid',
  seriesBreakOutsideShift: 'time-reports.series.break-outside-shift'
};

const DATE_RANGE_ERRORS: readonly SeriesErrorKind[] = ['seriesRangeRequired', 'seriesInvalidRange', 'seriesTooLong'];
const WEEKDAY_ERRORS: readonly SeriesErrorKind[] = ['seriesWeekdaysRequired', 'seriesNoDays'];

function defaultModel(): ReportSeriesModel {
  return {
    enabled: false,
    dateRange: { start: null, end: null },
    weekdays: [...DEFAULT_SERIES_WEEKDAYS],
    breakEnabled: false,
    breakMinutes: DEFAULT_SERIES_BREAK_MINUTES
  };
}

/**
 * ReportSeriesForm - Shared state, validation and request building for the multi-day series
 * option of the work-time and absence dialogs (Signal Forms).
 *
 * Must be created in an injection context (a component field initializer). Both dialogs create one
 * instance and render it with {@link ReportSeriesSectionComponent}, so controls and rules are identical.
 * The series is opt-in: it starts switched off, and while it is off (or unavailable) the dialogs keep
 * their regular single-report path untouched.
 */
export class ReportSeriesForm {
  private readonly feature = inject(TimeReportSeriesFeatureService);
  private readonly locale = inject(LOCALE_ID);
  private readonly context: ReportSeriesContext;

  public readonly model = signal<ReportSeriesModel>(defaultModel());
  /** Set by the host on a save attempt so errors show even for untouched fields. */
  public readonly submitted = signal(false);

  /** Series controls are shown only when the test-API gate is open and the host allows it. */
  public readonly available: Signal<boolean> = computed(() => this.feature.available() && this.context.canUse());
  public readonly active: Signal<boolean> = computed(() => this.available() && this.model().enabled);
  public readonly rangeMode: Signal<boolean> = computed(() => !!this.context.dailyRange());
  public readonly effectiveBreakMinutes: Signal<number> = computed(() => {
    const value = this.model();
    return this.rangeMode() && value.breakEnabled ? Math.max(0, value.breakMinutes) : 0;
  });
  public readonly errorKind: Signal<SeriesErrorKind | undefined> = computed(() => {
    if (!this.active()) {
      return undefined;
    }
    const value = this.model();
    return validateSeriesInput({
      dateFrom: value.dateRange.start,
      dateTo: value.dateRange.end,
      weekdays: value.weekdays,
      dailyRange: this.context.dailyRange(),
      breakMinutes: this.effectiveBreakMinutes()
    });
  });

  public readonly form = form(this.model, path => {
    validate(path.dateRange, () => this.errorFor(DATE_RANGE_ERRORS));
    validate(path.weekdays, () => this.errorFor(WEEKDAY_ERRORS));
    validate(path.breakMinutes, () =>
      this.errorKind() === 'seriesBreakOutsideShift' ? { kind: 'seriesBreakOutsideShift' } : undefined
    );
  });

  /** Valid when the series is off, or when every series rule passes. */
  public readonly valid: Signal<boolean> = computed(() => !this.active() || !this.errorKind());
  public readonly occurrenceDates: Signal<Date[]> = computed(() => {
    if (!this.active()) {
      return [];
    }
    const value = this.model();
    return seriesOccurrenceDates(value.dateRange.start, value.dateRange.end, value.weekdays);
  });
  /** Only a series with two or more days uses the per-day series writes. */
  public readonly isMultiDay: Signal<boolean> = computed(() => this.valid() && this.occurrenceDates().length > 1);
  /** The one day of an active one-day series; the host then uses its regular single POST for it. */
  public readonly singleDate: Signal<Date | undefined> = computed(() => {
    const dates = this.occurrenceDates();
    return this.valid() && dates.length === 1 ? dates[0] : undefined;
  });

  public readonly showErrors: Signal<boolean> = computed(() => this.submitted() || this.form().touched());
  public readonly dateRangeErrorKey: Signal<string> = computed(() => this.visibleErrorKey(DATE_RANGE_ERRORS));
  public readonly weekdaysErrorKey: Signal<string> = computed(() => this.visibleErrorKey(WEEKDAY_ERRORS));
  public readonly dailyRangeErrorKey: Signal<string> = computed(() =>
    this.visibleErrorKey(['seriesInvalidDailyRange', 'seriesBreakOutsideShift'])
  );
  public readonly suggestedBreakStartLabel: Signal<string> = computed(() => {
    const daily = this.context.dailyRange();
    if (!daily || !this.effectiveBreakMinutes()) {
      return '';
    }
    const start = suggestedBreakStart(daily.from, dailyRangeMinutes(daily.from, daily.to));
    return start ? toClockValue(start).slice(0, 5) : '';
  });
  public readonly periodLabel: Signal<string> = computed(() => {
    const { start, end } = this.model().dateRange;
    if (!start || !end) {
      return '';
    }
    return `${formatDate(start, 'd MMM y', this.locale)} – ${formatDate(end, 'd MMM y', this.locale)}`;
  });

  public constructor(context: ReportSeriesContext) {
    this.context = context;
  }

  public setEnabled(enabled: boolean): void {
    this.submitted.set(false);
    this.model.update(current => ({
      ...current,
      enabled,
      dateRange: enabled ? { ...this.context.initialRange() } : current.dateRange
    }));
  }

  public isWeekdaySelected(weekday: SeriesWeekday): boolean {
    return this.model().weekdays.includes(weekday);
  }

  public toggleWeekday(weekday: SeriesWeekday): void {
    this.model.update(current => ({
      ...current,
      weekdays: current.weekdays.includes(weekday)
        ? current.weekdays.filter(value => value !== weekday)
        : [...current.weekdays, weekday]
    }));
    this.form.weekdays().markAsTouched();
  }

  public setBreakEnabled(enabled: boolean): void {
    this.model.update(current => ({
      ...current,
      breakEnabled: enabled,
      breakMinutes: current.breakMinutes > 0 ? current.breakMinutes : DEFAULT_SERIES_BREAK_MINUTES
    }));
    this.form.breakMinutes().markAsTouched();
  }

  public setBreakMinutes(minutes: number | null): void {
    const value = Math.min(MAX_SERIES_BREAK_MINUTES, Math.max(0, Math.round(minutes ?? 0)));
    this.model.update(current => ({ ...current, breakEnabled: value > 0, breakMinutes: value }));
    this.form.breakMinutes().markAsTouched();
  }

  /** Reveals validation on a save attempt (the DS date range picker shows its error once its field is touched). */
  public markSubmitted(): void {
    this.submitted.set(true);
    this.form.dateRange().markAsTouched();
  }

  public reset(): void {
    this.model.set(defaultModel());
    this.submitted.set(false);
  }

  /**
   * Builds the series write request. `manualHoursPerDay` is the ISO duration of one Manual day;
   * in Range mode the host's daily from/to define each day (gross, the break is preview only).
   */
  public buildSeries(manualHoursPerDay: string | undefined): TimeReportSeries {
    const value = this.model();
    const daily = this.context.dailyRange();
    const series: TimeReportSeries = {
      localDateFrom: toLocalIsoDate(value.dateRange.start!),
      localDateTo: toLocalIsoDate(value.dateRange.end!),
      includedWeekdays: includedWeekdayMask(value.weekdays),
      hoursPerDay: daily
        ? (minutesToIsoDuration(dailyRangeMinutes(daily.from, daily.to) ?? 0) ?? '')
        : (manualHoursPerDay ?? '')
    };
    if (daily) {
      series.dailyStartTime = toClockValue(daily.from);
      series.dailyEndTime = toClockValue(daily.to);
    }
    return series;
  }

  private errorFor(kinds: readonly SeriesErrorKind[]): { kind: SeriesErrorKind } | undefined {
    const kind = this.errorKind();
    return kind && kinds.includes(kind) ? { kind } : undefined;
  }

  private visibleErrorKey(kinds: readonly SeriesErrorKind[]): string {
    const kind = this.errorKind();
    return this.showErrors() && kind && kinds.includes(kind) ? SERIES_ERROR_KEYS[kind] : '';
  }
}
